import { useEffect, useRef, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Sparkles, Send, ChevronDown, ChevronRight, Trash2,
  Globe, Lock, Loader2, User, MessageCircleQuestion, FileDown, Table2,
} from "lucide-react";
import { api } from "../lib/api";
import { renderMd } from "../lib/markdown";
import { downloadCSV, downloadAnswerPDF } from "../lib/exports";

const PALETTE = ["#0a0a0a", "#c72c2c", "#047857", "#b45309", "#1d4ed8"];

/** Derive flat {columns, rows} from a viz spec for CSV/PDF export. */
function vizToTable(viz) {
  if (!viz || !Array.isArray(viz.data) || viz.data.length === 0) return null;
  if (viz.type === "table" && (viz.columns || []).length) {
    return { columns: viz.columns.map((c) => ({ key: c.key, label: c.label || c.key })), rows: viz.data };
  }
  if ((viz.type === "bar" || viz.type === "line") && (viz.series || []).length) {
    const x = viz.x || "label";
    const cols = [{ key: x, label: x }, ...viz.series.map((s) => ({ key: s.key, label: s.label || s.key }))];
    return { columns: cols, rows: viz.data };
  }
  return null;
}

/** Serialize an on-page recharts <svg> to a PNG data URL for PDF embedding. */
function svgToPng(svg, scale = 2) {
  return new Promise((resolve, reject) => {
    try {
      const rect = svg.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      const clone = svg.cloneNode(true);
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      clone.setAttribute("width", w);
      clone.setAttribute("height", h);
      const xml = new XMLSerializer().serializeToString(clone);
      const src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = w * scale;
        canvas.height = h * scale;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#f5f2eb";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        resolve({ png: canvas.toDataURL("image/png"), w, h });
      };
      img.onerror = reject;
      img.src = src;
    } catch (e) { reject(e); }
  });
}

function fmtVal(v, f) {
  if (v == null || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (f === "currency" && !Number.isNaN(n)) return `$${Math.round(n).toLocaleString()}`;
  if (f === "percent" && !Number.isNaN(n)) return `${n > 0 ? "+" : ""}${n}%`;
  if (f === "number" && !Number.isNaN(n)) return n.toLocaleString();
  return String(v);
}

function AnalystViz({ viz }) {
  if (!viz || viz.type === "none" || !Array.isArray(viz.data) || viz.data.length === 0) return null;
  const data = viz.data.slice(0, 15);

  if (viz.type === "table") {
    const cols = viz.columns || [];
    return (
      <div className="mt-3 border border-[#0a0a0a]/40 bg-[#f5f2eb] overflow-x-auto" data-testid="analyst-viz-table">
        {viz.title && <div className="bm-eyebrow text-neutral-500 px-3 pt-3">{viz.title}</div>}
        <table className="w-full text-sm mt-1">
          <thead>
            <tr className="border-b border-[#0a0a0a]/30">
              {cols.map((c) => (
                <th key={c.key} className="text-left bm-eyebrow text-neutral-600 px-3 py-2 font-normal whitespace-nowrap">{c.label || c.key}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} className="border-b border-[#0a0a0a]/10 last:border-0">
                {cols.map((c) => (
                  <td key={c.key} className={`px-3 py-1.5 ${c.format && c.format !== "text" ? "font-mono-bm tabular-nums" : ""}`}>
                    {fmtVal(row[c.key], c.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const series = (viz.series || []).filter((s) => s && s.key);
  const xKey = viz.x || "label";
  const yFmt = (v) => fmtVal(v, series[0]?.format);
  const ChartEl = viz.type === "line" ? LineChart : BarChart;
  return (
    <div className="mt-3 border border-[#0a0a0a]/40 bg-[#f5f2eb] p-3" data-testid="analyst-viz-chart">
      {viz.title && <div className="bm-eyebrow text-neutral-500 mb-2">{viz.title}</div>}
      <ResponsiveContainer width="100%" height={260}>
        <ChartEl data={data} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="2 2" stroke="#0a0a0a20" vertical={false} />
          <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: "#525252" }} interval={0} angle={data.length > 6 ? -20 : 0} textAnchor={data.length > 6 ? "end" : "middle"} height={data.length > 6 ? 54 : 24} />
          <YAxis tick={{ fontSize: 11, fill: "#525252" }} tickFormatter={yFmt} width={54} />
          <Tooltip formatter={(v, name) => {
            const s = series.find((x) => (x.label || x.key) === name || x.key === name);
            return fmtVal(v, s?.format);
          }} contentStyle={{ fontSize: 12, border: "1px solid #0a0a0a", background: "#f5f2eb" }} />
          {series.map((s, i) => viz.type === "line" ? (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key} stroke={s.color || PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />
          ) : (
            <Bar key={s.key} dataKey={s.key} name={s.label || s.key} fill={s.color || PALETTE[i % PALETTE.length]} />
          ))}
        </ChartEl>
      </ResponsiveContainer>
    </div>
  );
}

function AnswerSources({ sources, idx }) {
  if (!Array.isArray(sources) || sources.length === 0) return null;
  return (
    <div className="mt-3 border-t border-[#0a0a0a]/15 pt-2" data-testid={`analyst-sources-${idx}`}>
      <div className="bm-eyebrow text-neutral-400 mb-1">Sources (web)</div>
      <ol className="space-y-1">
        {sources.map((s, i) => (
          <li key={s.url + i} className="text-xs flex items-baseline gap-2">
            <span className="font-mono-bm text-neutral-400 shrink-0">[{i + 1}]</span>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`analyst-source-link-${idx}-${i}`}
              className="underline decoration-[#0a0a0a]/40 hover:text-[#c72c2c] break-all"
            >
              {s.title || s.url}
            </a>
            {s.date && <span className="bm-eyebrow text-neutral-400 shrink-0">{s.date}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function AnswerBlock({ message, question, idx }) {
  const vizRef = useRef(null);
  const table = vizToTable(message.viz);
  const base = `bm-analyst-${(question || "answer").slice(0, 40)}`;

  function exportCSV() {
    if (!table) return;
    downloadCSV(base, table.columns, table.rows);
  }

  async function exportPDF() {
    let chart = null;
    const svg = vizRef.current?.querySelector("svg");
    if (svg) {
      try { chart = await svgToPng(svg); } catch { chart = null; }
    }
    downloadAnswerPDF(
      base,
      {
        title: question || message.narrative?.slice(0, 80) || "Analyst answer",
        narrative: (message.sources?.length
          ? `${message.narrative}\n\nSources (web):\n${message.sources.map((s, i) => `[${i + 1}] ${s.title} — ${s.url}`).join("\n")}`
          : message.narrative),
        chartPng: chart?.png,
        chartW: chart?.w,
        chartH: chart?.h,
      },
      table?.columns || [],
      table?.rows || [],
    );
  }

  return (
    <div className="border border-[#0a0a0a]/30 bg-white/40 p-3" data-testid={`analyst-msg-answer-${idx}`}>
      {message.research && (
        <div className="bm-eyebrow text-neutral-500 mb-2 flex items-center gap-1.5" data-testid={`analyst-research-badge-${idx}`}>
          <Globe size={11} className="text-[#c72c2c]" /> Includes live web research · your store data stays the source of truth
        </div>
      )}
      {renderMd(message.narrative)}
      <div ref={vizRef}>
        <AnalystViz viz={message.viz} />
      </div>
      <AnswerSources sources={message.sources} idx={idx} />
      <div className="hidden md:flex items-center gap-2 mt-3 pt-2 border-t border-[#0a0a0a]/15">
        <span className="bm-eyebrow text-neutral-400">Export</span>
        <button
          onClick={exportPDF}
          data-testid={`analyst-export-pdf-${idx}`}
          className="inline-flex items-center gap-1.5 px-2 py-1 bm-eyebrow border border-[#0a0a0a] hover:bg-[#0a0a0a]/5"
        >
          <FileDown size={12} /> PDF
        </button>
        <button
          onClick={exportCSV}
          disabled={!table}
          data-testid={`analyst-export-csv-${idx}`}
          className="inline-flex items-center gap-1.5 px-2 py-1 bm-eyebrow border border-[#0a0a0a] hover:bg-[#0a0a0a]/5 disabled:opacity-40 disabled:cursor-not-allowed"
          title={table ? "Download data as CSV" : "No table data to export"}
        >
          <Table2 size={12} /> CSV
        </button>
      </div>
    </div>
  );
}

function Conversation({ messages }) {
  let lastQuestion = "";
  return (
    <div className="space-y-3" data-testid="analyst-conversation">
      {messages.map((m, i) => {
        if (m.role === "user") {
          lastQuestion = m.text;
          return (
            <div key={i} className="flex items-start gap-2">
              <User size={15} className="text-neutral-500 mt-0.5 shrink-0" />
              <div className="text-sm font-medium text-[#0a0a0a]" data-testid={`analyst-msg-user-${i}`}>{m.text}</div>
            </div>
          );
        }
        if (m.kind === "clarify") {
          return (
            <div key={i} className="flex items-start gap-2 border-l-2 border-[#c72c2c] pl-3">
              <MessageCircleQuestion size={15} className="text-[#c72c2c] mt-0.5 shrink-0" />
              <div className="text-sm text-neutral-800" data-testid={`analyst-msg-clarify-${i}`}>{m.text}</div>
            </div>
          );
        }
        return <AnswerBlock key={i} message={m} question={lastQuestion} idx={i} />;
      })}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
}

export default function DataAnalyst({ outlets = [], outletIds = [] }) {
  const [input, setInput] = useState("");
  const [active, setActive] = useState(null); // {id, title, messages, status}
  const [busy, setBusy] = useState(false);
  const [threads, setThreads] = useState([]);
  const [expanded, setExpanded] = useState({}); // id -> full thread
  const [loadingId, setLoadingId] = useState(null);
  const scrollRef = useRef(null);

  const nameById = Object.fromEntries(outlets.map((o) => [o.id, o.name]));
  const filters = () => ({
    stores: (outletIds || []).map((id) => nameById[id]).filter(Boolean),
  });

  const loadThreads = () =>
    api.get("/analyst/threads").then(({ data }) => setThreads(data)).catch(() => {});

  useEffect(() => { loadThreads(); }, []);

  const awaitingClarify = active && active.status === "awaiting_clarification";

  async function submit() {
    const msg = input.trim();
    if (!msg || busy) return;
    setBusy(true);
    setInput("");
    try {
      const body = { message: msg, filters: filters() };
      if (awaitingClarify) body.thread_id = active.id;
      const { data } = await api.post("/analyst/ask", body);
      setActive(data.thread);
      loadThreads();
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 60);
    } catch (e) {
      setActive((a) => a || { id: "err", title: msg, status: "answered", messages: [
        { role: "user", text: msg }, { role: "assistant", kind: "answer", narrative: "_Couldn't reach the analyst. Try again._", viz: { type: "none" } },
      ] });
    } finally {
      setBusy(false);
    }
  }

  async function openThread(t) {
    if (expanded[t.id]) { setExpanded((e) => ({ ...e, [t.id]: undefined })); return; }
    setLoadingId(t.id);
    try {
      const { data } = await api.get(`/analyst/threads/${t.id}`);
      let full = data;
      // Lazily generate seeded/unanswered shared entries on first open
      if (!full.answered && full.messages?.length && full.messages[full.messages.length - 1].role === "user") {
        const run = await api.post(`/analyst/threads/${t.id}/run`, { filters: filters() });
        full = run.data.thread;
        loadThreads();
      }
      setExpanded((e) => ({ ...e, [t.id]: full }));
    } catch { /* no-op */ } finally { setLoadingId(null); }
  }

  async function toggleShare(t, e) {
    e.stopPropagation();
    const next = !t.shared;
    await api.post(`/analyst/threads/${t.id}/share`, { shared: next }).catch(() => {});
    loadThreads();
    setExpanded((ex) => ex[t.id] ? { ...ex, [t.id]: { ...ex[t.id], shared: next } } : ex);
  }

  async function del(t, e) {
    e.stopPropagation();
    if (!window.confirm("Delete this question and its answer?")) return;
    await api.delete(`/analyst/threads/${t.id}`).catch(() => {});
    setExpanded((ex) => { const c = { ...ex }; delete c[t.id]; return c; });
    if (active?.id === t.id) setActive(null);
    loadThreads();
  }

  const EXAMPLES = [
    "Which store is winning on accessories this quarter?",
    "What's my fastest-emerging category vs last year?",
    "Compare 347 and 256 revenue over the last 3 months",
  ];

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="data-analyst">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <div className="bm-eyebrow">Table 18 &middot; Ask the data</div>
      </div>
      <h3 className="bm-headline text-3xl mb-1">What insights are you looking for?</h3>
      <p className="bm-eyebrow text-neutral-500 mb-4">
        Ask anything about your sales &middot; Claude may ask a quick question (like which dates) before it answers &middot; questions about the wider market (&ldquo;how do we compare to Canadian clothing retail?&rdquo;) trigger live web research with cited sources &middot; saved to the library below
      </p>

      {/* Ask box */}
      <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-3">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="analyst-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
            rows={2}
            placeholder={awaitingClarify ? "Answer Claude's question…" : "e.g. How did our September compare to the overall Canadian clothing market?"}
            className="flex-1 resize-none bg-transparent text-sm outline-none placeholder:text-neutral-400 leading-relaxed"
          />
          <button
            data-testid="analyst-submit"
            onClick={submit}
            disabled={busy || !input.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 bm-eyebrow border border-[#0a0a0a] bg-[#0a0a0a] text-[#f5f2eb] hover:opacity-90 disabled:opacity-40 shrink-0"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            {busy ? "Thinking…" : awaitingClarify ? "Reply" : "Ask"}
          </button>
        </div>
        {!active && !busy && (
          <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-[#0a0a0a]/15">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                data-testid="analyst-example"
                onClick={() => setInput(ex)}
                className="bm-eyebrow text-neutral-600 border border-[#0a0a0a]/30 px-2 py-1 hover:bg-[#0a0a0a]/5"
              >
                {ex}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Active conversation */}
      {active && (
        <div ref={scrollRef} className="mt-4 border border-[#0a0a0a] bg-[#f5f2eb] p-4" data-testid="analyst-active">
          <div className="flex items-center justify-between mb-3">
            <div className="bm-eyebrow text-neutral-500">Current thread</div>
            <button data-testid="analyst-new" onClick={() => setActive(null)} className="bm-eyebrow text-neutral-500 hover:text-[#0a0a0a] underline">
              New question
            </button>
          </div>
          <Conversation messages={active.messages || []} />
          {busy && <div className="flex items-center gap-2 text-sm text-neutral-500 mt-3"><Loader2 size={14} className="animate-spin" /> Pulling the numbers…</div>}
        </div>
      )}

      {/* Library */}
      <div className="mt-6" data-testid="analyst-library">
        <div className="bm-eyebrow text-neutral-500 mb-2 flex items-center gap-2">
          <Sparkles size={12} /> Library &middot; {threads.length} saved
        </div>
        {threads.length === 0 && (
          <div className="text-sm text-neutral-500">No saved questions yet. Ask one above.</div>
        )}
        <div className="divide-y divide-[#0a0a0a]/15 border-y border-[#0a0a0a]/20">
          {threads.map((t) => {
            const open = !!expanded[t.id];
            const full = expanded[t.id];
            return (
              <div key={t.id} data-testid={`analyst-lib-item-${t.id}`}>
                <button
                  onClick={() => openThread(t)}
                  className="w-full text-left py-3 flex items-start gap-3 hover:bg-[#0a0a0a]/[0.03] px-1"
                  data-testid={`analyst-lib-toggle-${t.id}`}
                >
                  {loadingId === t.id ? <Loader2 size={16} className="animate-spin mt-0.5 shrink-0 text-neutral-500" /> : open ? <ChevronDown size={16} className="mt-0.5 shrink-0 text-neutral-500" /> : <ChevronRight size={16} className="mt-0.5 shrink-0 text-neutral-500" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#0a0a0a] flex items-center gap-2 flex-wrap">
                      <span className="truncate">{t.title}</span>
                      {t.shared && <span className="bm-eyebrow text-emerald-700 inline-flex items-center gap-1"><Globe size={10} /> shared</span>}
                      {t.seeded && <span className="bm-eyebrow text-neutral-400">pinned</span>}
                      {!t.answered && <span className="bm-eyebrow text-neutral-400">tap to run</span>}
                    </div>
                    {t.preview && <div className="text-xs text-neutral-500 truncate mt-0.5">{t.preview}</div>}
                    <div className="bm-eyebrow text-neutral-400 mt-0.5 font-mono-bm">
                      {timeAgo(t.updated_at)}{!t.mine ? ` · ${t.creator}` : ""}
                    </div>
                  </div>
                  {t.can_edit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <span
                        role="button"
                        title={t.shared ? "Make private" : "Share with everyone"}
                        onClick={(e) => toggleShare(t, e)}
                        data-testid={`analyst-share-${t.id}`}
                        className="p-1.5 hover:bg-[#0a0a0a]/5 text-neutral-500"
                      >
                        {t.shared ? <Globe size={14} /> : <Lock size={14} />}
                      </span>
                      {!t.seeded && (
                        <span
                          role="button"
                          title="Delete"
                          onClick={(e) => del(t, e)}
                          data-testid={`analyst-delete-${t.id}`}
                          className="p-1.5 hover:bg-[#c72c2c]/10 text-[#c72c2c]"
                        >
                          <Trash2 size={14} />
                        </span>
                      )}
                    </div>
                  )}
                </button>
                {open && full && (
                  <div className="pb-4 px-1" data-testid={`analyst-lib-body-${t.id}`}>
                    <Conversation messages={full.messages || []} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
